import { Component, Input, OnInit } from "@angular/core";
import { AuthorizationService } from "../../../../platform-pages/src/lib/api/authorization/authorization.service";
import { AccountService } from "../../../../platform-pages/src/lib/pages/home-admin/home-admin-data/account.service";
import { Comment, CommentFromJo } from "../../../../platform-pages/src/lib/pages/home-admin/home-admin-data/account.types";
import { MatchedJobOpportunityAO } from "../../../../platform-pages/src/lib/api/matching/matching.types";
import { forkJoin, take } from "rxjs";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { NzModalService } from "ng-zorro-antd/modal";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "mh-account-comments",
  templateUrl: "./account-comments.component.html",
  styleUrls: ["./account-comments.component.less"],
})
export class AccountCommentsComponent implements OnInit {
  @Input() talentId!: string;
  formGroup: FormGroup;
  authorId: string | undefined;
  comments: (Comment & Partial<CommentFromJo>)[] = [];
  editingCommentId: string | null = null;
  
  matchedJobOpportunities: MatchedJobOpportunityAO[] = [];
  isLoadingMatches = false;

  constructor(
    private authorizationService: AuthorizationService,
    private accountService: AccountService,
    private modal: NzModalService,
    private translateService: TranslateService,
  ) {
    this.authorizationService.getAccountId().subscribe((accountId) => {
      this.authorId = accountId;
    });
    this.formGroup = new FormGroup({
      destination: new FormControl("notes", [Validators.required]),
      selectedJobId: new FormControl(null),
      text: new FormControl("", [Validators.required]),
    });
    
    this.formGroup.get("destination")?.valueChanges.subscribe((value) => {
      const selectedJobIdControl = this.formGroup.get("selectedJobId");
      if (!selectedJobIdControl) return;

      if (value === "job-match") {
        selectedJobIdControl.setValidators([Validators.required]);
        this.loadJobMatches();
      } else {
        selectedJobIdControl.clearValidators();
        selectedJobIdControl.reset();
      }
      selectedJobIdControl.updateValueAndValidity();
    });
  }

  loadJobMatches() {
    if (this.matchedJobOpportunities.length === 0 && !this.isLoadingMatches) {
      this.isLoadingMatches = true;
      this.accountService
        .loadMatchedJobOpportunities(this.talentId)
        .pipe(take(1))
        .subscribe((matches) => {
          this.matchedJobOpportunities = matches || [];
          this.isLoadingMatches = false;
        });
    }
  }

  ngOnInit() {
    this.loadComments();
  }

  loadComments() {
    forkJoin([
      this.accountService.loadAccountComments(this.talentId).pipe(take(1)),
      this.accountService.loadAccountCommentsFromJobOpportunities(this.talentId).pipe(take(1))
    ]).subscribe(([notes, joComments]) => {
      this.comments = [...notes, ...joComments].sort((a, b) => {
        return new Date(a.creationTimestamp).getTime() - new Date(b.creationTimestamp).getTime();
      });
    });
  }

  handleSubmit(): void {
    if (this.formGroup.invalid) {
      this.markFormGroupTouched();
      return;
    }

    const text = this.formGroup.get("text")?.value;
    const destination = this.formGroup.get("destination")?.value;
    const selectedJobId = this.formGroup.get("selectedJobId")?.value;

    if (this.editingCommentId) {
      this.updateComment(this.editingCommentId, text);
      return;
    } 
    
    if (destination === "job-match" && selectedJobId) {
      this.addJobMatchComment(selectedJobId, text);
      return;
    } 
    
    this.addComment(text);
  }

  private markFormGroupTouched(): void {
    Object.values(this.formGroup.controls).forEach((control) => {
      if (control.invalid) {
        control.markAsDirty();
        control.updateValueAndValidity({ onlySelf: true });
      }
    });
  }

  onSearchJobOffer() {
    const control = this.formGroup.get('selectedJobId');
    if (control && control.invalid && control.dirty) {
      control.markAsPristine();
      control.markAsUntouched();
      control.updateValueAndValidity({ onlySelf: true });
    }
  }

  addJobMatchComment(jobOpportunityId: string, text: string) {
    if (this.talentId) {
      this.accountService.sendAccountCommentToJO(this.talentId, jobOpportunityId, text).subscribe((comment) => {
        if (comment) {
          this.formGroup.reset({ destination: 'notes' });
          this.loadComments();
        }
      });
    }
  }

  addComment(text: string) {
    if (this.talentId && this.authorId) {
      this.accountService.sendAccountComment(this.talentId, text, this.authorId).subscribe((comment) => {
        if (comment) {
          this.formGroup.reset({ destination: 'notes' });
          this.loadComments();
        }
      });
    }
  }

  updateComment(commentId: string, text: string) {
    const comment = this.comments.find((c) => c.commentId === commentId);
    if (!comment) return;

    const onSuccess = (updatedComment: any) => {
      if (!updatedComment) return;

      const index = this.comments.findIndex((c) => c.commentId === commentId);
      if (index !== -1) {
        this.comments[index].text = text;
      }
      this.formGroup.reset({ destination: 'notes' });
      this.editingCommentId = null;
    };

    if (comment.jobOpportunityId) {
      this.accountService.updateJobOfferTalentComment(commentId, text).subscribe(onSuccess);
      return;
    } 
    
    this.accountService.updateAccountComment(commentId, text).subscribe(onSuccess);
  }

  startEditing(comment: Comment & Partial<CommentFromJo>) {
    this.editingCommentId = comment.commentId;
    this.formGroup.patchValue({ text: comment.text });
  }

  cancelEditing() {
    this.editingCommentId = null;
    this.formGroup.reset({ destination: 'notes' });
  }

  confirmDeleteComment(comment: Comment & Partial<CommentFromJo>) {
    this.modal.confirm({
      nzTitle: this.translateService.instant("comment.confirm.delete.label"),
      nzOkText: this.translateService.instant("ok.button"),
      nzCancelText: this.translateService.instant("cancel.button"),
      nzOkType: "primary",
      nzOkDanger: true,
      nzOnOk: () => this.onDeleteComment(comment),
    });
  }

  onDeleteComment(comment: Comment & Partial<CommentFromJo>) {
    const request = comment.jobOpportunityId
      ? this.accountService.deleteJobOfferTalentComment(comment.jobOpportunityId, comment.commentId)
      : this.accountService.deleteAccountComment(comment.commentId);

    request
      .pipe(take(1))
      .subscribe((success) => {
        if (success) {
          this.comments = this.comments.filter((c) => c.commentId !== comment.commentId);
        }
      });
  }
}
